import React, { useState } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { Shield, Book, Eye, Edit, User, Mail, Users, Send, X, Check, Lock, UserPlus } from 'lucide-react';


// The backend already resolves these (middlewares/loadCallerContext.js):
//
//   PAYROLL_ROLES  = admin, owner, payroll_admin
//   HR_ADMIN_ROLES = admin, owner, hr_admin
//
// so ticking payroll_admin next to Admin has never granted anything Admin did
// not already grant. Manager is deliberately NOT in that list — managers act
// through the time-bounded contract_assignment.manager_employee_id relation,
// not a blanket capability — so it stays a normal choice.
//
// This only labels the redundant clicks. Roles remain free multi-select and the
// submitted payload is exactly what the user ticks.
const GRANTS_EVERYTHING = new Set(['admin', 'owner']);

// Owner is a singleton with a dedicated transfer path (RoleService §2.4).
// The invite path does NOT enforce that — inviteUser accepts any role array and
// acceptInvitation inserts it straight through assignRoles — so ticking Owner
// here produces a second Owner row, after which updateUserRoles refuses every
// role edit for the whole company ("Company has multiple Owner rows"). Locked
// in the picker until the backend guards it and the transfer flow exists.
const NOT_GRANTABLE_BY_INVITE = new Set(['owner']);
const COVERED_BY_ADMIN = new Set(['payroll_admin', 'hr_admin']);

const InviteUserModal = ({ show, handleClose, allRoles, handleInviteUser }) => {
    const [email, setEmail] = useState('');
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [emailError, setEmailError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleEmailChange = (e) => {
        const newEmail = e.target.value;
        setEmail(newEmail);

        // Clear error when user starts typing
        if (emailError) {
            setEmailError('');
        }

        // Validate email format
        if (newEmail && !validateEmail(newEmail)) {
            setEmailError('Ange en giltig e-postadress');
        }
    };

    const adminSelected = selectedRoles.some((r) => GRANTS_EVERYTHING.has(r.toLowerCase()));
    // Covered only while the user has not ticked it themselves — an existing
    // choice is never silently taken away.
    const isCovered = (roleName) => adminSelected
        && COVERED_BY_ADMIN.has(roleName.toLowerCase())
        && !selectedRoles.includes(roleName);

    const handleRoleChange = (roleName) => {
        if (selectedRoles.includes(roleName)) {
            setSelectedRoles(selectedRoles.filter((role) => role !== roleName));
        } else {
            setSelectedRoles([...selectedRoles, roleName]);
        }
    };

    const handleSubmit = async () => {
        // Validate form
        if (!email) {
            setEmailError('E-postadress är obligatorisk');
            return;
        }

        if (!validateEmail(email)) {
            setEmailError('Ange en giltig e-postadress');
            return;
        }

        if (selectedRoles.length === 0) {
            return; // Don't submit if no roles selected
        }

        setIsLoading(true);
        try {
            await handleInviteUser(email, selectedRoles);
            // Reset form on success
            setEmail('');
            setSelectedRoles([]);
            setEmailError('');
        } catch (error) {
            setEmailError('Inbjudan kunde inte skickas. Försök igen om en stund.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleModalClose = () => {
        // Reset form when closing
        setEmail('');
        setSelectedRoles([]);
        setEmailError('');
        handleClose();
    };

    // Role descriptions for better UX
    const getRoleDescription = (roleName) => {
        const descriptions = {
            'Admin': 'Full tillgång till alla funktioner och inställningar',
            'Bookkeeper': 'Kan hantera bokföring och rapporter',
            'Viewer': 'Kan endast visa rapporter och data',
            'Editor': 'Kan redigera data men inte systeminställningar'
        };
        return descriptions[roleName] || 'Användarroll';
    };


    // Replace the emoji-based function with this SVG icon approach
    const getRoleIcon = (roleName) => {
        switch (roleName.toLowerCase()) {
            case 'admin':
                return <Shield size={16} />;
            case 'bookkeeper':
                return <Book size={16} />;
            case 'viewer':
                return <Eye size={16} />;
            case 'editor':
                return <Edit size={16} />;
            default:
                return <User size={16} />;
        }
    };
    return (
        <>
            <style>{`
                .modern-modal .modal-content {
                    border: none;
                    border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
                    overflow: hidden;
                }
                
                .modern-modal .modal-header {
                    background: #f9fafb;
                    color: #374151;
                    border: none;
                    border-bottom: 1px solid #e5e7eb;
                    padding: 1.25rem 1.5rem;
                    position: relative;
                }
                
                .modern-modal .modal-title {
                    font-size: 1rem;
                    font-weight: 600;
                    margin: 0;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                
                .modern-modal .modal-title svg {
                    width: 16px;
                    height: 16px;
                    color: #6b7280;
                }
                
                .modern-modal .btn-close {
                    background: none;
                    border: none;
                    color: #64748b;
                    opacity: 0.8;
                    font-size: 1.5rem;
                    transition: all 0.3s ease;
                }
                
                .modern-modal .btn-close:hover {
                    opacity: 1;
                    color: #374151;
                    transform: scale(1.1);
                }
                
                .modern-modal .modal-body {
                    padding: 1.5rem;
                    background: #f9fafb;
                }
                
                .modern-modal .modal-footer {
                    background: white;
                    border-top: 1px solid #e5e7eb;
                    padding: 1rem 1.5rem;
                    display: flex;
                    gap: 1rem;
                    justify-content: flex-end;
                }
                
                .form-group-modern {
                    margin-bottom: 1.5rem;
                }
                
                .form-label-modern {
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 0.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.875rem;
                }
                
                .form-control-modern {
                    border: 2px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 0.75rem 1rem;
                    font-size: 0.875rem;
                    transition: all 0.3s ease;
                    background: white;
                }
                
                .form-control-modern:focus {
                    border-color: var(--primary);
                    box-shadow: 0 0 0 3px var(--rgba-primary-1);
                    outline: none;
                }
                
                .form-control-modern.error {
                    border-color: #ef4444;
                    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
                }
                
                .error-message {
                    color: #ef4444;
                    font-size: 0.75rem;
                    margin-top: 0.25rem;
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                }
                
                .roles-section {
                    background: white;
                    border-radius: 12px;
                    padding: 1rem 1.25rem;
                    border: 1px solid #e5e7eb;
                }
                
                .roles-header {
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.875rem;
                }
                
                /* Rows, not a bordered card per role inside a bordered box
                   inside the modal body — three levels of frame for a checkbox
                   list. One rule between rows is enough. */
                .role-item {
                    padding: 0.625rem 0.5rem;
                    border: 0;
                    border-bottom: 1px solid #f3f4f6;
                    border-radius: 6px;
                    margin-bottom: 0;
                    transition: background-color 0.15s ease;
                    cursor: pointer;
                    background: none;
                }
                
                .role-item:last-of-type {
                    border-bottom: 0;
                }
                
                /* Already granted by Admin/Owner — shown so it is visibly
                   handled, rather than offered as a click that does nothing. */
                .role-item.is-covered {
                    cursor: default;
                    opacity: 0.7;
                }
                
                /* Roles as toggle pills rather than a stacked list: eight of
                   them fit in two rows, so the picker no longer scrolls. The
                   description moves to the pill's tooltip and stays in the DOM
                   for screen readers. */
                .role-pills {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.5rem;
                }
                
                .role-pills .role-item {
                    display: inline-flex;
                    padding: 0;
                    border: 0;
                    border-radius: 999px;
                }
                
                /* The pill's colour lives on .role-header, and the selected
                   green is only 10% opaque — so the row-level hover background
                   inherited from the list layout showed straight through it and
                   washed a selected pill out to near-white. The wrapper stays
                   transparent in pill mode. */
                .role-pills .role-item,
                .role-pills .role-item:hover {
                    background: none;
                }
                
                .role-pills .role-header {
                    gap: 0.375rem;
                    padding: 0.3125rem 0.75rem;
                    border: 1px solid #d5d9e0;
                    border-radius: 999px;
                    background: #fff;
                    transition: border-color 0.15s ease, background-color 0.15s ease;
                }
                
                /* :not(.selected) matters: a :hover counts as a class for
                   specificity, so the hover rule outranked the selected rule
                   and a pill you had just clicked stayed grey until the
                   pointer left it. */
                .role-pills .role-item:hover:not(.is-covered):not(.selected) .role-header {
                    border-color: #b6bcc7;
                    background: #f9fafb;
                }
                
                .role-pills .role-item.selected .role-header {
                    border-color: var(--primary);
                    background: var(--rgba-primary-1);
                    color: var(--primary);
                }
                
                .role-pills .role-item.selected:hover .role-header {
                    background: var(--rgba-primary-2);
                }
                
                .role-pills .role-item.selected .role-icon {
                    color: var(--primary);
                }
                
                .role-pills .role-name {
                    font-size: 0.8125rem;
                    font-weight: 500;
                }
                
                /* Kept in the DOM for assistive tech; the pill shows it on hover. */
                .role-pills .role-description,
                .role-pills .role-checkbox {
                    position: absolute;
                    width: 1px;
                    height: 1px;
                    margin: -1px;
                    padding: 0;
                    overflow: hidden;
                    clip: rect(0 0 0 0);
                    white-space: nowrap;
                    border: 0;
                }
                
                .role-pills .flex-grow-1 {
                    flex-grow: 0 !important;
                }
                
                .role-locked {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                    flex-shrink: 0;
                    padding: 0.125rem 0.4375rem;
                    font-size: 0.6875rem;
                    font-weight: 600;
                    color: #92400e;
                    background: #fef3c7;
                    border-radius: 999px;
                }
                
                .role-covered {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                    flex-shrink: 0;
                    padding: 0.125rem 0.4375rem;
                    font-size: 0.6875rem;
                    font-weight: 600;
                    color: var(--primary);
                    background: var(--rgba-primary-1);
                    border-radius: 999px;
                }
                
                .role-item:hover {
                    background: #f9fafb;
                }
                
                .role-item.selected {
                    background: var(--rgba-primary-1);
                }
                
                .role-header {
                    display: flex;
                    align-items: center;
                    gap: 0.625rem;
                    margin-bottom: 0;
                }
                
                .role-name {
                    font-weight: 600;
                    color: #1e293b;
                    font-size: 0.875rem;
                }
                
                .role-description {
                    color: #64748b;
                    font-size: 0.75rem;
                    line-height: 1.4;
                }
                
                .role-checkbox {
                    margin-left: auto;
                    transform: scale(1.2);
                }
                
                /* Same rule as the settings card headers: 16px, grey, no tile.
                   The glyph labels the row, it does not compete with it. */
                .role-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    color: #6b7280;
                }
                
                .role-icon svg {
                    width: 16px;
                    height: 16px;
                }
                
                .modern-button {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.5rem;
                    border-radius: 8px;
                    font-weight: 500;
                    font-size: 0.875rem;
                    border: none;
                    transition: all 0.3s ease;
                    cursor: pointer;
                    text-decoration: none;
                }
                
                .modern-button.primary {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-hover) 100%);
                    color: white;
                }
                
                .modern-button.primary:hover:not(:disabled) {
                    background: linear-gradient(135deg, var(--primary-hover) 0%, var(--primary-dark) 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px var(--rgba-primary-3);
                }
                
                .modern-button.primary:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
                
                .modern-button.secondary {
                    background: #f1f5f9;
                    color: #64748b;
                    border: 1px solid #e2e8f0;
                }
                
                .modern-button.secondary:hover {
                    background: #e2e8f0;
                    color: #475569;
                }
                
                .invite-summary {
                    background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
                    border: 1px solid #10b981;
                    border-radius: 8px;
                    padding: 1rem;
                    margin-top: 1rem;
                }
                
                .invite-summary-title {
                    font-weight: 600;
                    color: #059669;
                    font-size: 0.875rem;
                    margin-bottom: 0.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                
                .invite-summary-content {
                    color: #065f46;
                    font-size: 0.75rem;
                }
                
                .loading-spinner {
                    display: inline-block;
                    width: 16px;
                    height: 16px;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-radius: 50%;
                    border-top-color: white;
                    animation: spin 1s ease-in-out infinite;
                }
                
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>

            <Modal show={show} onHide={handleModalClose} className="modern-modal" centered>
                <Modal.Header>
                    <Modal.Title>
                        <UserPlus size={24} />
                        Bjud in ny användare
                    </Modal.Title>
                    <Button
                        variant="link"
                        className="btn-close p-0"
                        onClick={handleModalClose}
                        aria-label="Stäng"
                    >
                        <X size={24} />
                    </Button>
                </Modal.Header>

                <Modal.Body>
                    <Form>
                        <div className="form-group-modern">
                            <label className="form-label-modern">
                                <Mail size={16} />
                                E-postadress
                            </label>
                            <Form.Control
                                type="email"
                                placeholder="exempel@företag.se"
                                value={email}
                                onChange={handleEmailChange}
                                className={`form-control-modern ${emailError ? 'error' : ''}`}
                            />
                            {emailError && (
                                <div className="error-message">
                                    <X size={12} />
                                    {emailError}
                                </div>
                            )}
                        </div>

                        <div className="roles-section">
                            <div className="roles-header">
                                <Users size={16} />
                                Välj roller ({selectedRoles.length} valda)
                            </div>

                            <div className="role-pills">
                            {allRoles.map((role) => {
                                const locked = NOT_GRANTABLE_BY_INVITE.has(role.role_name.toLowerCase());
                                const covered = !locked && isCovered(role.role_name);
                                return (
                                    <div
                                        key={role.role_id}
                                        className={`role-item ${selectedRoles.includes(role.role_name) ? 'selected' : ''} ${covered || locked ? 'is-covered' : ''}`}
                                        onClick={() => !covered && !locked && handleRoleChange(role.role_name)}
                                        title={locked
                                            ? 'Ägarskap ges via ägarbyte, inte genom en inbjudan.'
                                            : covered
                                                ? `${getRoleDescription(role.role_name)} — ingår redan i Admin`
                                                : getRoleDescription(role.role_name)}
                                    >
                                        <div className="role-header">
                                            <div className="role-icon">
                                                {getRoleIcon(role.role_name)}
                                            </div>
                                            <div className="flex-grow-1">
                                                <div className="role-name">{role.role_name}</div>
                                                <div className="role-description">
                                                    {getRoleDescription(role.role_name)}
                                                </div>
                                            </div>
                                            {locked ? (
                                                <span className="role-locked">
                                                    <Lock size={12} /> Via ägarbyte
                                                </span>
                                            ) : covered ? (
                                                <span className="role-covered">
                                                    <Check size={12} /> Ingår i Admin
                                                </span>
                                            ) : (
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={selectedRoles.includes(role.role_name)}
                                                    onChange={() => handleRoleChange(role.role_name)}
                                                    className="role-checkbox"
                                                />
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            </div>

                            {selectedRoles.length === 0 && (
                                <Alert variant="warning" className="mt-2 mb-0">
                                    <small>Du måste välja minst en roll för användaren.</small>
                                </Alert>
                            )}
                        </div>

                        {email && selectedRoles.length > 0 && (
                            <div className="invite-summary">
                                <div className="invite-summary-title">
                                    <Check size={16} />
                                    Inbjudan redo att skickas
                                </div>
                                <div className="invite-summary-content">
                                    <strong>{email}</strong> kommer att få tillgång med rollerna: {selectedRoles.join(', ')}
                                </div>
                            </div>
                        )}
                    </Form>
                </Modal.Body>

                <Modal.Footer>
                    <button
                        className="modern-button secondary"
                        onClick={handleModalClose}
                        disabled={isLoading}
                    >
                        Avbryt
                    </button>
                    <Button className='' variant="primary"
                        onClick={handleSubmit}
                        disabled={!email || !validateEmail(email) || selectedRoles.length === 0 || isLoading}>
                        {isLoading ? (
                            <>
                                <div className="loading-spinner" />
                                Skickar...
                            </>
                        ) : (
                            <>
                                <Send className="me-2" size={16} />
                                Skicka inbjudan
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default InviteUserModal;


// import React, { useState } from 'react';
// import { Modal, Form, Button } from 'react-bootstrap';



// const InviteUserModal = ({ show, handleClose, allRoles, handleInviteUser }) => {
//     const [email, setEmail] = useState('');
//     const [selectedRoles, setSelectedRoles] = useState([]);

//     const handleRoleChange = (e) => {
//         const roleName = e.target.name;
//         if (e.target.checked) {
//             setSelectedRoles([...selectedRoles, roleName]);
//         } else {
//             setSelectedRoles(selectedRoles.filter((role) => role !== roleName));
//         }
//     };

//     const handleSubmit = () => {
//         handleInviteUser(email, selectedRoles);
//     };

//     return (
//         <Modal show={show} onHide={handleClose}>
//             <Modal.Header closeButton>
//                 <Modal.Title>Bjud in ny användare</Modal.Title>
//             </Modal.Header>
//             <Modal.Body>
//                 <Form>
//                     <Form.Group controlId="inviteEmail">
//                         <Form.Label>E-postadress</Form.Label>
//                         <Form.Control
//                             type="email"
//                             placeholder="Ange e-postadress"
//                             value={email}
//                             onChange={(e) => setEmail(e.target.value)}
//                         />
//                     </Form.Group>
//                     <Form.Label className="mt-3">Välj roller</Form.Label>
//                     {allRoles.map((role) => (
//                         <Form.Group controlId={`invite-role-${role.role_name}`} key={role.role_id}>
//                             <Form.Check
//                                 type="checkbox"
//                                 label={role.role_name}
//                                 name={role.role_name}
//                                 onChange={handleRoleChange}
//                             />
//                         </Form.Group>
//                     ))}
//                 </Form>
//             </Modal.Body>
//             <Modal.Footer>
//                 <Button variant="secondary" onClick={handleClose}>Avbryt</Button>
//                 <Button variant="primary" onClick={handleSubmit}>Skicka inbjudan</Button>
//             </Modal.Footer>
//         </Modal>
//     );
// };

// export default InviteUserModal;
